import { createReduxModule } from "hooks-for-redux";
import {auth} from '../Auth/Firebase';
import { bottomAlert } from "../Components/Notifications/ToastAlerts";
import { config } from "../config";
import { postCall } from "../Helpers/Api";
import { getCookie, hasCookie, setCookie } from "../Helpers/Cookie";
import { user } from "./User";

const PENDING_SIGNUP_PROFILE_KEY = "saleassist_pending_signup_profile";
const PENDING_SIGNUP_PROFILE_TTL_MS = 10 * 60 * 1000;

export function setPendingSignupProfile(profile) {
  try {
    sessionStorage.setItem(
      PENDING_SIGNUP_PROFILE_KEY,
      JSON.stringify({
        ...profile,
        created_at: Date.now(),
      })
    );
  } catch (err) {
    // Signup should continue even if browser storage is unavailable.
  }
}

function getPendingSignupProfile() {
  try {
    const profile = sessionStorage.getItem(PENDING_SIGNUP_PROFILE_KEY);
    if (!profile) {
      return null;
    }

    const parsedProfile = JSON.parse(profile);
    if (
      !parsedProfile.created_at ||
      Date.now() - parsedProfile.created_at > PENDING_SIGNUP_PROFILE_TTL_MS
    ) {
      clearPendingSignupProfile();
      return null;
    }

    return parsedProfile;
  } catch (err) {
    clearPendingSignupProfile();
    return null;
  }
}

export function clearPendingSignupProfile() {
  try {
    sessionStorage.removeItem(PENDING_SIGNUP_PROFILE_KEY);
  } catch (err) {
    // Ignore storage cleanup failures.
  }
}

async function applyPendingSignupProfile(userAuth) {
  const pendingProfile = getPendingSignupProfile();
  if (!pendingProfile) {
    return {
      userAuth,
      displayNameForAuth: "",
    };
  }

  const pendingEmail = (pendingProfile.email || "").toLowerCase();
  const currentEmail = (userAuth.email || "").toLowerCase();

  if (pendingEmail && pendingEmail !== currentEmail) {
    return {
      userAuth,
      displayNameForAuth: "",
    };
  }

  clearPendingSignupProfile();
  const displayNameForAuth = pendingProfile.displayName || "";

  if (pendingProfile.displayName && !userAuth.displayName) {
    await userAuth.updateProfile({
      displayName: pendingProfile.displayName,
    });
    await userAuth.reload();
    return {
      userAuth: auth.currentUser || userAuth,
      displayNameForAuth,
    };
  }

  return {
    userAuth,
    displayNameForAuth,
  };
}

export const [token, {setAuthenticated, setkeycloak, setAuthUser, setToken, resetToken}, tokenStore] = createReduxModule(
  "token",
  {
    keycloak: null,
    isAuthenticated: false,
    user: null,
    token: {
      token: "",
      refresh_token: "",
      expire_at: 0
    }
  },
  {
    resetToken: (token) => {token.token = {token: "", refresh_token: "", expire_at: 0 }; return token},
    setToken: (token, token_object) => {token.token = token_object; return token},
    setAuthenticated: (token, auth_state) => {token.isAuthenticated = auth_state; return token},
    setkeycloak: (token, keycloak) => {token.keycloak = keycloak; return token},
    setAuthUser: (token, user) => {token.user = user; return token}
  }
);

export function isAuthenticated() {
  return tokenStore.getState()["isAuthenticated"];
}

export function getKeycloak() {
  return tokenStore.getState()["keycloak"];
}

export function getAuthUser() {
  return tokenStore.getState()["user"]
}

export function getToken() {
  return tokenStore.getState()["token"]
}

export function needLogin({onSuccess = () => {}, onFailure = () => {}}) {
  if(hasCookie("saleassist_auth_token")) {
    setToken(getCookie("saleassist_auth_token"));
    setAuthenticated(true);
    onSuccess(true);
    return;
  }

  auth.onAuthStateChanged(async userAuth => {
    if (userAuth) {
      try {
        const pendingSignupProfile = await applyPendingSignupProfile(userAuth);
        userAuth = pendingSignupProfile.userAuth;
        setAuthUser(userAuth);
        const token = await userAuth.getIdToken(true);
        postCall({
          url: config.url.AUTH,
          data: {
            token: token,
            display_name: pendingSignupProfile.displayNameForAuth,
          },
          onSuccess: (response) => {
            setToken(response.data);
            setAuthenticated(true);
            // setCookie("saleassist_auth_token", response.data);
            onSuccess(true);
          },
          onFailure: (error) => {
            bottomAlert(error.toString(), "warning");
            setAuthUser(null);
            resetToken();
            setAuthenticated(false);
            onSuccess(false);    
          }
        });
      } catch (error) {
        bottomAlert(error.toString(), "warning");
        setAuthUser(null);
        resetToken();
        setAuthenticated(false);
        onSuccess(false);
      }
    } else {
      setAuthUser(null);
      setToken("");
      setAuthenticated(false);
      onSuccess(false);
    }
  });
}
